import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Municipality, getYearlyDataForYear, getYearlyTotal } from '@/lib/data';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { useFeeCategories } from '@/contexts/FeeCategoryContext';

interface CountyComparisonChartProps {
  municipality: Municipality;
  allMunicipalities: Municipality[];
  selectedYear: number;
}

const CountyComparisonChart: React.FC<CountyComparisonChartProps> = ({
  municipality,
  allMunicipalities,
  selectedYear,
}) => {
  const { enabledCategories } = useFeeCategories();
  const countyMunicipalities = allMunicipalities
    .filter((m) => m.county === municipality.county)
    .map((m) => {
      const yearData = getYearlyDataForYear(m, selectedYear);
      return {
        ...m,
        totalFees: yearData ? getYearlyTotal(yearData, enabledCategories) : 0,
      };
    })
    .filter((m) => m.totalFees > 0)
    .sort((a, b) => b.totalFees - a.totalFees);

  if (countyMunicipalities.length === 0) return null;

  const chartData = countyMunicipalities.map((m) => ({
    name: m.name,
    nameShort: m.name.length > 12 ? m.name.slice(0, 10) + '…' : m.name,
    totalFees: m.totalFees,
    isCurrent: m.id === municipality.id,
  }));

  const maxFees = Math.max(...chartData.map((d) => d.totalFees));

  return (
    <div className="bg-white p-4 md:p-5 rounded-lg shadow-sm border border-slate-100">
      <h3 className="text-lg font-semibold mb-4">
        Kommuner i {municipality.county} ({selectedYear})
      </h3>
      <div className="h-64 md:h-72 min-h-[200px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
            <XAxis
              type="number"
              tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
              tick={{ fontSize: 11 }}
              domain={[0, maxFees * 1.05]}
            />
            <YAxis
              type="category"
              dataKey="nameShort"
              width={100}
              tick={{ fontSize: 11 }}
              tickLine={false}
            />
            <Tooltip
              content={({ active, payload }) => {
                if (!active || !payload?.[0]) return null;
                const p = payload[0].payload;
                return (
                  <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-sm">
                    <p className="font-semibold">{p.name}</p>
                    <p className="text-nordicBlue font-medium">
                      {p.totalFees.toLocaleString('nb-NO')} kr/år
                    </p>
                  </div>
                );
              }}
            />
            <Bar dataKey="totalFees" radius={[0, 4, 4, 0]} maxBarSize={24}>
              {chartData.map((entry, index) => (
                <Cell
                  key={index}
                  fill={entry.isCurrent ? '#4375C0' : '#94a3b8'}
                  stroke={entry.isCurrent ? '#4375C0' : undefined}
                  strokeWidth={entry.isCurrent ? 2 : 0}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <ul className="mt-4 space-y-1">
        {countyMunicipalities.slice(0, 8).map((m) => (
          <li key={m.id}>
            <Link
              to={`/municipality/${m.id}`}
              className={`flex items-center justify-between py-1.5 px-2 rounded-md text-sm transition-colors ${
                m.id === municipality.id
                  ? 'bg-nordicBlue/10 text-nordicBlue font-medium'
                  : 'hover:bg-gray-50 text-gray-700'
              }`}
            >
              <span>
                {m.name}
                {m.id === municipality.id && (
                  <span className="ml-2 text-xs">(denne)</span>
                )}
              </span>
              <span className="flex items-center gap-1">
                {m.totalFees.toLocaleString('nb-NO')} kr
                <ChevronRight className="h-4 w-4 opacity-50" />
              </span>
            </Link>
          </li>
        ))}
      </ul>
      {countyMunicipalities.length > 8 && (
        <p className="text-xs text-muted-foreground mt-2">
          + {countyMunicipalities.length - 8} kommuner til i {municipality.county}
        </p>
      )}
    </div>
  );
};

export default CountyComparisonChart;
